// Publish — route /publish
//
// The approval queue: ready videos the operator reviews and uploads to a
// connected YouTube channel. Also hosts channel connect/disconnect. The factory
// (scheduler) will feed videos here; a human picks a channel + visibility and
// clicks 게시. YouTube OAuth client credentials are set in Settings.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { Link2, Trash2, Upload, Youtube } from 'lucide-react'
import { api, ApiError } from '@/api/client'
import type { PublishAccount, PublishQueueItem, PublishVisibility } from '@/api/types'
import { Button, Card, EmptyState, Input, Select, Textarea } from '@/admin-design/_common'

export default function Publish() {
  const qc = useQueryClient()
  const [params, setParams] = useSearchParams()
  const accountsQ = useQuery({ queryKey: ['publish-accounts'], queryFn: api.listPublishAccounts })
  const queueQ = useQuery({ queryKey: ['publish-queue'], queryFn: api.publishQueue })

  const deleteAccount = useMutation({
    mutationFn: (id: number) => api.deletePublishAccount(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['publish-accounts'] }),
  })

  const accounts = accountsQ.data ?? []
  const queue = queueQ.data ?? []
  const connected = params.get('connected') === '1'
  const oauthError = params.get('error')

  return (
    <div className="space-y-4">
      <header className="flex items-center gap-3">
        <h1 className="text-xl font-semibold text-foreground">Publish</h1>
        <span className="text-sm text-muted-foreground">{queue.length}개 대기</span>
        <Button
          type="button"
          variant="primary"
          size="sm"
          className="ml-auto"
          onClick={() => {
            window.location.href = '/api/publish/youtube/connect'
          }}
        >
          <Youtube size={14} /> YouTube 채널 연결
        </Button>
      </header>

      {connected && (
        <p className="rounded border border-success-soft bg-success-soft p-2 text-xs text-success-soft-foreground">
          채널이 연결되었습니다.{' '}
          <button className="underline" onClick={() => setParams({})}>닫기</button>
        </p>
      )}
      {oauthError && (
        <p className="rounded border border-destructive-soft bg-destructive-soft p-2 text-xs text-destructive-soft-foreground">
          연결 실패: {oauthError}. Settings에서 YouTube OAuth 클라이언트를 확인하세요.{' '}
          <button className="underline" onClick={() => setParams({})}>닫기</button>
        </p>
      )}

      {/* Connected channels */}
      <Card>
        <header className="flex items-center gap-2 border-b border-border px-3 py-2">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-card-foreground">
            <Link2 size={14} /> 연결된 채널
          </span>
          <span className="text-[11px] text-muted-foreground">{accounts.length}개</span>
        </header>
        <div className="p-3">
          {accounts.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              연결된 채널이 없어요. Settings에서 YouTube OAuth 클라이언트를 저장한 뒤 위 “채널 연결”을 누르세요.
            </p>
          ) : (
            <ul className="space-y-1">
              {accounts.map((a) => (
                <li key={a.id} className="flex items-center gap-2 text-sm text-card-foreground">
                  <Youtube size={14} className="text-destructive" />
                  {a.channel_title || a.external_channel_id}
                  <button
                    onClick={() => deleteAccount.mutate(a.id)}
                    className="ml-auto p-1 text-muted-foreground hover:text-destructive"
                    aria-label="disconnect"
                    disabled={deleteAccount.isPending}
                  >
                    <Trash2 size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>

      {/* Approval queue */}
      <Card className="overflow-hidden">
        {queueQ.isLoading ? (
          <p className="p-6 text-sm text-muted-foreground">불러오는 중…</p>
        ) : queue.length === 0 ? (
          <EmptyState
            title="게시 대기 중인 영상이 없어요."
            description="렌더가 완료된 영상이 여기에 나타납니다."
          />
        ) : (
          <div className="space-y-3 p-3">
            {queue.map((item) => (
              <QueueCard key={item.video_id} item={item} accounts={accounts} />
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}

function QueueCard({ item, accounts }: { item: PublishQueueItem; accounts: PublishAccount[] }) {
  const qc = useQueryClient()
  const [title, setTitle] = useState(item.title)
  const [description, setDescription] = useState(item.description)
  const [tags, setTags] = useState(item.tags.join(', '))
  const [visibility, setVisibility] = useState<PublishVisibility>('private')
  const [accountId, setAccountId] = useState<number | null>(accounts[0]?.id ?? null)

  const publish = useMutation({
    mutationFn: () =>
      api.createPublishJob({
        video_id: item.video_id,
        account_id: accountId ?? 0,
        visibility,
        title: title.trim(),
        description: description.trim(),
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['publish-queue'] }),
  })

  const err = publish.isError ? errorMessage(publish.error) : null
  const canPublish = Boolean(accountId) && !publish.isPending

  return (
    <div className="grid gap-3 rounded-md border border-border bg-muted/20 p-3 sm:grid-cols-[220px_1fr]">
      <video src={item.video_path} controls className="w-full rounded bg-black" />
      <div className="space-y-2">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="제목" />
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="설명" />
        <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="태그 (쉼표 구분)" />
        <div className="flex flex-wrap items-center gap-2">
          <Select
            className="w-40"
            value={accountId ? String(accountId) : ''}
            onValueChange={(v) => setAccountId(Number(v))}
            placeholder={accounts.length ? '채널 선택' : '연결된 채널 없음'}
            options={accounts.map((a) => ({ label: a.channel_title || a.external_channel_id, value: String(a.id) }))}
          />
          <Select
            className="w-32"
            value={visibility}
            onValueChange={(v) => setVisibility(v as PublishVisibility)}
            options={[
              { label: '비공개', value: 'private' },
              { label: '일부공개', value: 'unlisted' },
              { label: '공개', value: 'public' },
            ]}
          />
          <Button
            type="button"
            variant="primary"
            size="sm"
            className="ml-auto"
            onClick={() => publish.mutate()}
            disabled={!canPublish}
            loading={publish.isPending}
          >
            <Upload size={14} /> 게시
          </Button>
        </div>
        {publish.isSuccess && (
          <p className="text-xs text-success">업로드 시작됨. 완료되면 큐에서 사라집니다.</p>
        )}
        {err && <p className="text-xs text-destructive">{err}</p>}
      </div>
    </div>
  )
}

function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message
  if (e instanceof Error) return e.message
  return 'unknown error'
}
